import React, { useEffect, useState } from "react";
import CustomListing from "../components/CustomListing";
import "../styles/layout.css";
import ReusableHeader from "../components/ReusableHeader";
import ReusableFilter from "../components/ReusableFilter";
import { toast } from "react-toastify";
import CustomButton from "../components/CustomButton";
import { Filter, Info } from "lucide-react";
import { formatDateOnly } from "../util/date";
import { useSubscriptionApi } from "../api/subscription";

export default function DeviceSubscriptions() {
  const {
    getAllSubscriptions,
    enableSubscription,
    extendSubscription,
  } = useSubscriptionApi();

  const [rows, setRows] = useState([]);
  const [isFilterOpen, setIsFilterOpen] = useState(false);

  // Filters
  const [selectedManufacturer, setSelectedManufacturer] = useState("");
  const [selectedStatus, setSelectedStatus] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");

  const [filterObject, setFilterObject] = useState({});
  const [searchText, setSearchText] = useState("");

  // CHAIN STEP INFO DIALOG
  const [stepDetails, setStepDetails] = useState(null);

  // EXTEND MODAL
  const [extendRow, setExtendRow] = useState(null);
  const [extendFrom, setExtendFrom] = useState("");
  const [extendTo, setExtendTo] = useState("");
  const [extendGrace, setExtendGrace] = useState(30);

const todayStr = new Date().toISOString().split("T")[0];

  // ---------------- INITIAL LOAD ----------------
  useEffect(() => {
    fetchWithFilters({ limit: 10, offset: 0 });
  }, []);

  // ---------------- SEARCH ----------------
  useEffect(() => {
    const delay = setTimeout(() => {
      fetchWithFilters({ limit: 10, offset: 0 });
    }, 500);

    return () => clearTimeout(delay);
  }, [searchText, filterObject]);

  // ---------------- FETCH ----------------
  const fetchWithFilters = async (params = {}) => {
    try {
      const res = await getAllSubscriptions({
        ...params,
        ...filterObject,
        search: searchText,
        deliveredOnly: true,
      });

      if (res?.success) setRows(res.data);
      return res;
    } catch {
      toast.error("Failed to load subscriptions");
    }
  };


const addOneDay = dateStr => {
  const d = new Date(dateStr);
  d.setDate(d.getDate() + 1);
  return d.toISOString().split("T")[0];
};

const subtractOneDay = dateStr => {
  const d = new Date(dateStr);
  d.setDate(d.getDate() - 1);
  return d.toISOString().split("T")[0];
};
  // ---------------- EXTEND HANDLER ----------------
  const handleExtend = row => {
    const today = new Date();
    const from = today.toISOString().split("T")[0];

    const nextYear = new Date();
    nextYear.setFullYear(today.getFullYear() + 1);
    const to = nextYear.toISOString().split("T")[0];

    setExtendFrom(from);
    setExtendTo(to);
    setExtendGrace(30);
    setExtendRow(row);
  };

  // ---------------- CONFIRM EXTEND ----------------
  const confirmExtend = async () => {
  if (extendTo <= extendFrom) {
    toast.error("Valid Till must be at least 1 day after Valid From");
    return;
  }

    try {
      await extendSubscription({
        deviceId: extendRow._id,
        validFrom: extendFrom,
        validThrough: extendTo,
        gracePeriodDays: extendGrace,
      });

      toast.success("Subscription extended");
      setExtendRow(null);
      fetchWithFilters({ limit: 10, offset: 0 });
    } catch (e) {
      toast.error(e.message || "Failed to extend subscription");
    }
  };

  // ---------------- ENABLE ----------------
  const handleEnable = async row => {
    try {
      await enableSubscription({ deviceId: row._id });
      toast.success("Subscription enabled");
      fetchWithFilters({ limit: 10, offset: 0 });
    } catch {
      toast.error("Failed to enable subscription");
    }
  };

  // ---------------- COLUMNS ----------------
  // ---------------- COLUMNS ----------------
const StatusPill = ({ text, type }) => {
  const colors = {
    success: "#d1fae5",
    warning: "#fef3c7",
    danger: "#fee2e2",
  };

  return (
    <span
      style={{
        padding: "4px 10px",
        borderRadius: 20,
        fontWeight: 600,
        fontSize: 12,
        background: colors[type] || colors.success,
        textTransform: "capitalize",
      }}
    >
      {text}
    </span>
  );
};

const columns = [
  // ---------- DEVICE INFO ----------
  {
    key: "deviceInfo",
    label: "Device",
    render: row => (
      <div style={{ lineHeight: 1.6 }}>
        <div style={{ fontWeight: 700 }}>
          BCU: {row.serialNumber}
        </div>

        <div style={{ fontSize: 13, color: "#6b7280" }}>
          Machine: {row.machineSerialNumber}
        </div>

        <div style={{ fontSize: 12, color: "#9ca3af" }}>
          {row.macAddress}
        </div>
      </div>
    ),
  },

  // ---------- DELIVERY CHAIN ----------
  {
    key: "deliveryChain",
    label: "Delivery Chain",
    render: row => {
      const chain = row.deliveryChain || [];
      if (!chain.length) return <span style={{ color: "#9ca3af" }}>-</span>;

      return (
        <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
          {chain.map((step, i) => {
            const hasDetails = step.role !== "Admin" && (step.phoneNumber || step.address);
            return (
              <div key={i} style={{ display: "flex", flexDirection: "column", alignItems: "flex-start" }}>
                <div style={{ fontSize: 13, display: "flex", alignItems: "center", gap: 6 }}>
                  <span style={{ fontWeight: 600 }}>{step.userName || step.name || "-"}</span>
                  <span style={{ color: "#6b7280" }}>({step.role})</span>
                  {hasDetails && (
                    <button
                      type="button"
                      onClick={() => setStepDetails(step)}
                      style={{
                        background: "none",
                        border: "none",
                        cursor: "pointer",
                        padding: 0,
                        display: "flex",
                        alignItems: "center",
                        color: "#6b7280",
                      }}
                      title="View details"
                    >
                      <Info size={14} />
                    </button>
                  )}
                </div>
                {i !== chain.length - 1 && (
                  <span style={{ color: "#9ca3af", fontSize: 14, lineHeight: 1 }}>↓</span>
                )}
              </div>
            );
          })}
        </div>
      );
    },
  },

  // ---------- OWNER ----------
  {
    key: "owner",
    label: "Owner",
    render: row => (
      <div>
        <div style={{ fontWeight: 600 }}>
          {row.owner?.name || "-"}
        </div>
        <div style={{ fontSize: 12, color: "#6b7280" }}>
          {row.owner?.phoneNumber}
        </div>
      </div>
    ),
  },

  // ---------- BALES ----------
  {
    key: "totalBales",
    label: "Bales",
    render: row => (
      <div style={{ fontSize: 20, fontWeight: 700 }}>
        {row.totalBales ?? 0}
      </div>
    ),
  },

  // ---------- MAINTENANCE ----------
 {
  key: "maintenance",
  label: "Maintenance",
  render: row => {
    const getNextCheckup = () => {
      if (!row.nextCheckupAt) return "Not scheduled";

      // if number → show bales
      if (!isNaN(row.nextCheckupAt)) {
        return `${row.nextCheckupAt} bales`;
      }

      // if date → format
      return formatDateOnly(row.nextCheckupAt);
    };

    return (
      <div style={{ lineHeight: 1.8 }}>
        <StatusPill
          text={row.maintenanceStatus}
          type={
            row.maintenanceStatus === "OK"
              ? "success"
              : row.maintenanceStatus === "ALERT"
              ? "warning"
              : "danger"
          }
        />

        <div style={{ fontSize: 13 }}>
          Last:{" "}
          {row.lastCheckupDate
            ? formatDateOnly(row.lastCheckupDate)
            : "No checkup yet"}
        </div>

        <div style={{ fontSize: 13 }}>
          Next Checkup At: {getNextCheckup()}
        </div>
      </div>
    );
  },
},

  // ---------- SUBSCRIPTION ----------
  {
    key: "subscription",
    label: "Subscription",
    render: row => (
      <div style={{ lineHeight: 1.8 }}>
        <StatusPill
          text={row.subscriptionStatus}
          type={
            row.subscriptionStatus === "active"
              ? "success"
              : row.subscriptionStatus === "grace"
              ? "warning"
              : "danger"
          }
        />

        <div style={{ fontSize: 13 }}>
          {row.validFrom ? formatDateOnly(row.validFrom) : "-"} →{" "}
          {row.validThrough ? formatDateOnly(row.validThrough) : "-"}
        </div>

        <div style={{ fontSize: 12, color: "#6b7280" }}>
          Grace till{" "}
          {row.gracePeriodEnd
            ? formatDateOnly(row.gracePeriodEnd)
            : "-"}
        </div>
      </div>
    ),
  },

  // ---------- ACTION ----------
  {
    key: "actions",
    label: "Action",
    render: row => (
      <CustomButton
        label="Extend"
        onClick={() => handleExtend(row)}
      />
    ),
  },
];

  // ---------------- HEADER ----------------
  const renderActionButtons = () => (
    <div className="header-buttons">
      <input
        type="text"
        placeholder="Search subscriptions..."
        value={searchText}
        onChange={e => setSearchText(e.target.value)}
        className="search-input"
      />

      <CustomButton
        className="header-btn"
        label={<Filter size={16} />}
        onClick={() => setIsFilterOpen(true)}
      />
    </div>
  );

  // ---------------- APPLY FILTER ----------------
  const handleApplyFilters = async () => {
    const newFilters = {};

    if (selectedManufacturer)
      newFilters.manufacturerId = selectedManufacturer;
    if (selectedStatus) newFilters.status = selectedStatus;
    if (fromDate) newFilters.fromDate = fromDate;
    if (toDate) newFilters.toDate = toDate;

    setFilterObject(newFilters);
    setIsFilterOpen(false);
    fetchWithFilters({ limit: 10, offset: 0 });
  };

  // ---------------- UI ----------------
  return (
    <div className="inventoryManagement">
      <ReusableHeader
        title="Device Subscriptions"
        children={renderActionButtons()}
      />

      <CustomListing
        showCheckbox={false}
        data={rows}
        setData={setRows}
        fetchData={fetchWithFilters}
        columns={columns}
        pageSize={10}
        tableHeight="calc(100vh - 180px)"
        postData={{ search: searchText, ...filterObject }}
      />

      {/* ================= FILTER MODAL ================= */}
      <ReusableFilter
        isOpen={isFilterOpen}
        onClose={() => setIsFilterOpen(false)}
        title="Filter Subscriptions"
      >
        <div className="extend-modal">
          <div className="extend-grid">
            <div className="extend-field full-width">
              <label>Subscription Status</label>
              <select
                value={selectedStatus}
                onChange={(e) => setSelectedStatus(e.target.value)}
                style={{ padding: 8, borderRadius: 6, border: "1px solid #d1d5db" }}
              >
                <option value="">All</option>
                <option value="active">Active</option>
                <option value="grace">Grace</option>
                <option value="expired">Expired</option>
                <option value="disabled">Disabled</option>
              </select>
            </div>

            <div className="extend-field">
              <label>From Date</label>
              <input
                type="date"
                value={fromDate}
                max={toDate || undefined}
                onChange={(e) => setFromDate(e.target.value)}
              />
            </div>

            <div className="extend-field">
              <label>To Date</label>
              <input
                type="date"
                value={toDate}
                min={fromDate || undefined}
                onChange={(e) => setToDate(e.target.value)}
              />
            </div>
          </div>

          <div className="extend-actions">
            <button
              className="extend-confirm"
              onClick={handleApplyFilters}
            >
              Apply
            </button>

            <button
              className="extend-cancel"
              onClick={() => {
                setSelectedStatus("");
                setFromDate("");
                setToDate("");
                setFilterObject({});
                setIsFilterOpen(false);
                fetchWithFilters({ limit: 10, offset: 0 });
              }}
            >
              Reset
            </button>
          </div>
        </div>
      </ReusableFilter>

      {/* ================= CHAIN STEP DETAILS ================= */}
      {stepDetails && (
        <div className="modal-overlay" onClick={() => setStepDetails(null)}>
          <div className="modal-card" onClick={(e) => e.stopPropagation()}>
            <h3 style={{ marginTop: 0 }}>{stepDetails.role} Details</h3>
            <p><strong>Name:</strong> {stepDetails.userName || "-"}</p>
            {stepDetails.organizationName && (
              <p><strong>Organization:</strong> {stepDetails.organizationName}</p>
            )}
            <p><strong>Phone:</strong> {stepDetails.phoneNumber || "-"}</p>
            <p><strong>Address:</strong> {stepDetails.address || "-"}</p>
            <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 16 }}>
              <button
                onClick={() => setStepDetails(null)}
                style={{
                  background: "var(--primary-color, #006337)",
                  color: "#fff",
                  border: "none",
                  padding: "8px 20px",
                  borderRadius: 6,
                  cursor: "pointer",
                  fontSize: 14,
                  fontWeight: 500,
                }}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ================= EXTEND MODAL ================= */}

      {extendRow && (
        <ReusableFilter
          isOpen={true}
          onClose={() => setExtendRow(null)}
          title="Extend Subscription"
        >
          <div className="extend-modal">
            <div className="extend-grid">
              <div className="extend-field">
                <label>Valid From</label>
                <input
                  type="date"
                  value={extendFrom}
                  max={extendTo ? subtractOneDay(extendTo) : undefined}
                  min={todayStr}
                  onChange={e => {
                    const newFrom = e.target.value;

                    setExtendFrom(newFrom);

                    // Auto-fix To if invalid
                    if (extendTo && extendTo <= newFrom) {
                      setExtendTo(addOneDay(newFrom));
                    }
                  }}
                />
              </div>

              <div className="extend-field">
                <label>Valid Till</label>
              <input
                type="date"
                value={extendTo}
                min={extendFrom ? addOneDay(extendFrom) : todayStr}
                onChange={e => setExtendTo(e.target.value)}
              />
              </div>

              <div className="extend-field full-width">
                <label>Grace Period (days)</label>
                <input
                  type="number"
                  min={0}
                  value={extendGrace}
                  onChange={e => setExtendGrace(Number(e.target.value))}
                />
              </div>
            </div>

            <div className="extend-actions">
              <button className="extend-confirm" onClick={confirmExtend}>
                Confirm Extend
              </button>

              <button
                className="extend-cancel"
                onClick={() => setExtendRow(null)}
              >
                Cancel
              </button>
            </div>
          </div>
        </ReusableFilter>
      )}
    </div>
  );
}